import { Desktop24Regular } from "@fluentui/react-icons";
import { useCategories } from "hooks/useCategories";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { ITemplateDetailProps } from "models/ITemplate";
import React, { useMemo } from "react";
import { sanitizeTemplateContent } from "utils/generalUtils";
import { useQuickStartGalleryStyles } from "./quickStartGallery.styles";

export const TemplateDetail = React.memo(
  ({ template }: ITemplateDetailProps): JSX.Element => {
    const styles = useQuickStartGalleryStyles();

    const { data: categories } = useCategories();

    const kcMap = useMemo(() => {
      const map = new Map<string, string>();
      if (categories) {
        categories.forEach((category: ICategory) => {
          category.tags.forEach((kc: ITag) => {
            if (kc.id) {
              map.set(kc.id, kc.name);
            }
          });
        });
      }
      return map;
    }, [categories]);

    const knowledgeComponentNames = useMemo(() => {
      if (!template.suggestedKnowledgeComponents) {
        return [];
      }
      return template.suggestedKnowledgeComponents
        .map((id) => kcMap.get(id))
        .filter((name): name is string => name !== undefined);
    }, [template.suggestedKnowledgeComponents, kcMap]);

    const sanitizedTemplate = useMemo(
      () => ({
        name: sanitizeTemplateContent(template.name),
        description: template.description
          ? sanitizeTemplateContent(template.description)
          : undefined,
        overview: sanitizeTemplateContent(template.overview),
        recommendedLLM: sanitizeTemplateContent(template.recommendedLLM),
      }),
      [
        template.name,
        template.description,
        template.overview,
        template.recommendedLLM,
      ],
    );

    return (
      <div className={styles.templateDetail}>
        <h2 className={styles.templateDetailName}>{sanitizedTemplate.name}</h2>

        {sanitizedTemplate.description && (
          <p className={styles.templateDetailOverview}>
            {sanitizedTemplate.description}
          </p>
        )}

        {knowledgeComponentNames.length > 0 && (
          <div className={styles.templateDetailSection}>
            <div className={styles.templateDetailLabel}>
              Suggested Knowledge Components
            </div>
            <div className={styles.knowledgeList}>
              {knowledgeComponentNames.map((name) => (
                <span
                  key={sanitizeTemplateContent(name)}
                  className={styles.knowledgeTag}
                >
                  {sanitizeTemplateContent(name)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={styles.templateDetailSection}>
          <div className={styles.templateDetailLabel}>Selected LLM</div>
          <div className={styles.llmTag}>
            <Desktop24Regular className={styles.llmTagIcon} />
            {sanitizedTemplate.recommendedLLM}
          </div>
        </div>
      </div>
    );
  },
);
